'use-strict';

import { CONST } from 'Util/util';
import VZSpinner from 'Components/VZSpinner';

class FilterAndSortBar {
    constructor() {
        // Filter
        this.selectedFilters = [];
        this.isFilterBarSticky = false;
        this.mdBreakpoint = parseInt(CONST.breakpoints.lg, 10);

        this.spinner = document.querySelector('vz-spinner') || new VZSpinner();

        this.initializeFilterBar();
        this.initializeSorting();

        window.addEventListener('resize', () => this.updateFilterBarHeight());
        window.dataLayer = window.dataLayer || [];
    }

    /**
     * @function initialize
     * @private
     * @description initializes the file
     */
    initializeFilterBar() {
        this.filterBar = document.querySelector('.js-filter-bar');

        // Filter Bar is not configured correctly
        if (!this.filterBar) return;

        this.filterBarWrapper = document.querySelector('.js-filter-bar-wrapper');

        this.filters = this.filterBar.querySelectorAll('.js-filter');
        this.filtersWrapper = this.filterBar.querySelector('.js-filter-bar-content');
        this.allProductsFilter = this.filterBar.querySelector('.js-filter-reset');
        this.productList = document.querySelector('.js-product-listing');

        this.updateFilterBarHeight();

        // Bind the click event to the 'all products' button
        if (this.allProductsFilter) {
            this.allProductsFilter.addEventListener('click', () => {
                this.clearAllFilters();
                this.allProductsFilter.classList.add('active');
                this.selectedFilters = [];
                this.updateSelectedFiltersDataAttribute();
                this.applyChanges(this.allProductsFilter.getAttribute('data-href'));
            });
        }

        // Bind the click event to the rest of the filter buttons
        this.filters.forEach((filter) => {
            if (filter !== this.allProductsFilter) {
                filter.addEventListener('click', () => {
                    this.toggleFilter(filter, Array.from(this.filters).indexOf(filter));
                });
            }
        });

        if (this.allProductsFilter && !Array.from(this.filters).some((filter) => filter.classList.contains('active'))) {
            this.allProductsFilter.classList.add('active');
        }

        // Scroll event listeners
        this.filtersWrapper.addEventListener('scroll', () => this.handleFilterScroll());
        this.showFadingEffect();

        if (this.filterBarWrapper && this.filterBarWrapper.classList.contains('js-filter-bar-sticky')) {
            window.addEventListener('scroll', () => this.handleScroll());
        }
    }

    /**
     * @function initializeSorting
     * @private
     * @description Initializes the sorting functionality
     */
    initializeSorting() {
        this.sorting = document.querySelector('.js-filter-bar-sort');

        // Sorting is not configured correctly
        if (!this.sorting) return;

        this.sorting.addEventListener('change', (event) => {
            const selectedOption = event.target.options[event.target.selectedIndex];
            const url = selectedOption.value;
            this.applyChanges(url);
        });
    }

    /**
     * @function updateDom
     * @private
     * @param {Object} results - The parsed HTML response from the fetch request
     * @param {string} selector - The CSS selector of the DOM element to update
     * @description Updates the specified DOM element with the parsed HTML response
     * @returns {Promise} promise - Resolves when the DOM update is complete
     */
    static updateDom(results, selector) {
        return new Promise((resolve) => {
            const updates = results.querySelector(selector);
            const target = document.querySelector(selector);

            if (document.startViewTransition) {
                const transition = document.startViewTransition(() => {
                    target.innerHTML = updates.innerHTML;
                });

                transition.finished.then(resolve);
            } else {
                target.innerHTML = updates.innerHTML;
                resolve();
            }
        });
    }

    /**
     * @function updateBrowserURL
     * @static
     * @description Updates the browser URL with the value of the 'seoPermalink' from the document
     */
    static updateBrowserURL() {
        const seoPermaLink = document.querySelector('.seoPermalink')?.value;
        if (typeof seoPermaLink !== 'undefined') {
            window.history.pushState({ urlPath: seoPermaLink }, '', seoPermaLink);
        }
    }

    /**
     * @function parseResults
     * @private
     * @param {Object} response - The response from the fetch request
     * @description Updates the DOM with the parsed HTML response and re-initializes the event listeners on the DOM elements
     */
    parseResults(response) {
        const parser = new DOMParser();
        const results = parser.parseFromString(response, 'text/html');
        const elementsToUpdate = ['.js-product-listing', '.js-filter-bar-wrapper'];
        const domUpdatePromises = elementsToUpdate.map((selector) => FilterAndSortBar.updateDom(results, selector));

        Promise.all(domUpdatePromises).then(() => {
            this.initializeFilterBar();
            this.initializeSorting();
            FilterAndSortBar.updateBrowserURL();
            this.spinner.stop();
        });
    }

    /**
     * @function scrollToProductList
     * @private
     * @description Scrolls the window to the product list if it is past the top of the product list
     */
    scrollToProductList() {
        const productListRect = this.productList.getBoundingClientRect();
        const filterBarTopOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--filter-bar-top-offset').trim(), 10);
        const offsetHeight = this.filterBar.height + filterBarTopOffset;

        // Calculate the scroll amount and scroll if it is past the top of the product list
        const scrollAmount = productListRect.top - offsetHeight;
        if (scrollAmount < 0) {
            window.scrollBy({
                top: scrollAmount,
                behavior: 'smooth'
            });
        }
    }

    /**
     * @function clearAllFilters
     * @private
     * @description Clear all filters and remove the 'active' class
     */
    clearAllFilters() {
        this.filters.forEach((filter) => {
            filter.classList.remove('active');
        });
    }

    /**
     * @function toggleFilter
     * @private
     * @param {HTMLElement} filter - The filter to toggle
     * @param {number} index - The index of the filter
     * @description Toggle the active state of a filter
     */
    toggleFilter(filter, index) {
        filter.classList.toggle('active');
        this.activeFilters = Array.from(document.getElementsByClassName('filter-bar-btn active')).filter((btn) => !btn.classList.contains('js-filter-reset'));
        if (filter.classList.contains('active')) {
            this.selectedFilters.push(index);
            if (this.allProductsFilter) {
                this.allProductsFilter.classList.remove('active');
            }
        } else {
            this.selectedFilters = this.selectedFilters.filter((i) => i !== index);

            if (this.allProductsFilter && this.selectedFilters.length === 0) {
                this.allProductsFilter.classList.add('active');
            }
        }
        this.activeFiltersType = '';
        if (this.activeFilters.length > 0) {
            this.activeFiltersType = Array.from(this.activeFilters)
                .map((activeFilter) => activeFilter.getAttribute('data-value'))
                .join('_');
        }

        this.updateSelectedFiltersDataAttribute();
        this.applyChanges(filter.getAttribute('data-href'));

        window.dataLayer.push({
            event: 'mkb_solutions_filter',
            solutions_filter: this.activeFiltersType
        });
    }

    /**
     * @function updateSelectedFiltersDataAttribute
     * @private
     * @description Update the selected filters data attribute on the filter bar element
     */
    updateSelectedFiltersDataAttribute() {
        const value = this.selectedFilters.length > 0 ? this.selectedFilters.toString() : '';
        this.filterBar.setAttribute('data-selected-filters', value);
    }

    /**
     * @function handleFilterScroll
     * @private
     * @description Handles the horizontal scrolling event for the filters wrapper to show/hide the fading effect.
     */
    handleFilterScroll() {
        this.showFadingEffect();
    }

    /**
     * @function handleScroll
     * @private
     * @description Handles the global (vertical) scrolling event to check the sticky state of the filter bar.
     */
    handleScroll() {
        this.checkIfFilterBarIsSticky();
    }

    /**
     * @function checkIfFilterBarIsSticky
     * @private
     * @description Checks if the filter bar is in a sticky state based on its current position.
     */
    checkIfFilterBarIsSticky() {
        const style = window.getComputedStyle(this.filterBarWrapper);
        if (style.position !== 'sticky') {
            this.filterBarWrapper.classList.remove('is-sticky');
            return;
        }

        const rect = this.filterBarWrapper.getBoundingClientRect();
        const filterBarTopOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--filter-bar-top-offset').trim(), 10);
        const isCurrentlySticky = rect.top <= filterBarTopOffset;

        if (isCurrentlySticky !== this.isFilterBarSticky) {
            this.isFilterBarSticky = isCurrentlySticky;
            this.filterBarWrapper.classList.toggle('is-sticky', this.isFilterBarSticky);
        }
    }

    /**
     * @function showFadingEffect
     * @private
     * @description Toggles the fading effect classes based on the scroll state of the filters wrapper.
     */
    showFadingEffect() {
        const { scrollLeft, clientWidth, scrollWidth } = this.filtersWrapper;

        const overflowLeft = scrollLeft > 0;
        const overflowRight = scrollWidth - scrollLeft > clientWidth;

        this.filterBar.classList.toggle('fade-left', overflowLeft);
        this.filterBar.classList.toggle('fade-right', overflowRight);
    }

    /**
     * @function updateFilterBarHeight
     * @private
     * @description Updates the height of the filter bar based on its bounding rectangle
     */
    updateFilterBarHeight() {
        if (this.filterBar) this.filterBar.height = this.filterBar.getBoundingClientRect().height;
    }

    /**
     * @function applyChanges
     * @private
     * @param {string} url - The URL to fetch data from
     * @description Fetches the data from the provided URL and applies the changes (filtering or sorting)
     */
    applyChanges(url) {
        this.spinner.start();

        fetch(url)
            .then((response) => response.text())
            .then((response) => {
                const parser = new DOMParser();
                const results = parser.parseFromString(response, 'text/html');

                return FilterAndSortBar.updateDom(results, '.js-product-listing').then(() => this.parseResults(response));
            })
            .then(() => {
                this.scrollToProductList();
                this.spinner.stop();
            })
            .catch((error) => {
                this.spinner.stop();
                console.error('Error:', error);
            });
    }
}

(() => new FilterAndSortBar())();
